import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { TOUR_STEPS } from "./tourSteps";
import { stopTour, nextTourStep, prevTourStep } from "../tourSlice";
import "./adminTour.css";

const PAD = 8;

export default function AdminTour() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { active, stepIndex } = useSelector((state) => state.tour);

  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const retryRef = useRef(null);
  const tooltipRef = useRef(null);

  const step = active ? TOUR_STEPS[stepIndex] : null;
  const onLastStep = stepIndex >= TOUR_STEPS.length - 1;

  // Navigate to the step's page when it differs from the current route.
  useEffect(() => {
    if (!step) return;
    if (location.pathname !== step.page) {
      navigate(step.page);
    }
  }, [step, location.pathname, navigate]);

  // Locate + measure the target element, retrying briefly for post-navigation mounts.
  useLayoutEffect(() => {
    setRect(null);
    if (!step || location.pathname !== step.page) return;

    let tries = 0;
    const tryMeasure = () => {
      const el = document.getElementById(step.targetId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const measure = () => {
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        };
        measure();
        window.setTimeout(measure, 260); // settle after smooth scroll
        return true;
      }
      return false;
    };

    if (tryMeasure()) return undefined;

    retryRef.current = window.setInterval(() => {
      tries += 1;
      if (tryMeasure() || tries > 30) {
        window.clearInterval(retryRef.current);
      }
    }, 100);

    return () => window.clearInterval(retryRef.current);
  }, [step, location.pathname]);

  useEffect(() => {
    if (!rect) return;
    const onRecalc = () => {
      const el = step ? document.getElementById(step.targetId) : null;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    window.addEventListener("resize", onRecalc);
    window.addEventListener("scroll", onRecalc, true);
    return () => {
      window.removeEventListener("resize", onRecalc);
      window.removeEventListener("scroll", onRecalc, true);
    };
  }, [rect, step]);

  // Measure the tooltip's actual rendered size and clamp it fully inside the viewport,
  // re-running after every render (guarded) so it re-clamps on resize/content changes too.
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el) return;
    const ttRect = el.getBoundingClientRect();
    const gap = 16;
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top;
    let left;

    if (!rect) {
      top = vh / 2 - ttRect.height / 2;
      left = vw / 2 - ttRect.width / 2;
    } else {
      const placement = step?.placement || "bottom";
      switch (placement) {
        case "left":
          top = rect.top + rect.height / 2 - ttRect.height / 2;
          left = rect.left - gap - ttRect.width;
          break;
        case "right":
          top = rect.top + rect.height / 2 - ttRect.height / 2;
          left = rect.left + rect.width + gap;
          break;
        case "top":
          top = rect.top - gap - ttRect.height;
          left = rect.left + rect.width / 2 - ttRect.width / 2;
          break;
        default:
          top = rect.top + rect.height + gap;
          left = rect.left + rect.width / 2 - ttRect.width / 2;
      }
    }

    top = Math.min(Math.max(top, margin), vh - ttRect.height - margin);
    left = Math.min(Math.max(left, margin), vw - ttRect.width - margin);

    setTooltipPos((prev) => {
      if (prev && Math.abs(prev.top - top) < 0.5 && Math.abs(prev.left - left) < 0.5) return prev;
      return { top, left };
    });
  });

  if (!active || !step) return null;

  const handleSkip = () => dispatch(stopTour());
  const handleBack = () => dispatch(prevTourStep());
  const handleNext = () => {
    if (onLastStep) dispatch(stopTour());
    else dispatch(nextTourStep());
  };

  const spotlightStyle = rect
    ? {
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  const tooltipStyle = tooltipPos
    ? { top: tooltipPos.top, left: tooltipPos.left }
    : { top: -9999, left: -9999 };

  return (
    <div className="admin-tour-layer" role="dialog" aria-modal="true">
      <div className="admin-tour-scrim" onClick={handleSkip} />
      {spotlightStyle ? <div className="admin-tour-spotlight" style={spotlightStyle} /> : null}
      <div ref={tooltipRef} className="admin-tour-tooltip" style={tooltipStyle}>
        <div className="admin-tour-tooltip-step">
          Step {stepIndex + 1} of {TOUR_STEPS.length}
        </div>
        <h4 className="admin-tour-tooltip-title">{step.title}</h4>
        <p className="admin-tour-tooltip-content">{step.content}</p>
        <div className="admin-tour-tooltip-actions">
          <button type="button" className="admin-tour-btn admin-tour-btn--ghost" onClick={handleSkip}>
            Skip
          </button>
          <div className="admin-tour-tooltip-actions-right">
            {stepIndex > 0 && (
              <button type="button" className="admin-tour-btn admin-tour-btn--ghost" onClick={handleBack}>
                Back
              </button>
            )}
            <button type="button" className="admin-tour-btn admin-tour-btn--primary" onClick={handleNext}>
              {onLastStep ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
